// src/pages/DebtStudio/lib/buildPortfolioDebtService.js

import dayjs from "lib/dayjs";
import { buildDebtServiceSchedule } from "pages/DebtStudio/lib/buildDebtServiceSchedule";

/* ------------------------------------------------------------------
 * Helpers
 * ------------------------------------------------------------------ */

function safeJson(value, fallback) {
  if (value == null) return fallback;
  if (typeof value === "string") {
    try {
      return JSON.parse(value);
    } catch {
      return fallback;
    }
  }
  return value;
}

function num(x, dflt = 0) {
  const n = Number(x);
  return Number.isFinite(n) ? n : dflt;
}

/**
 * Round to nearest cent, returns Number (not string).
 * Use at aggregation boundaries to avoid float tails from repeated addition.
 */
function round2(x) {
  const n = num(x, 0);
  return Math.round((n + Number.EPSILON) * 100) / 100;
}

function maxDate(rows) {
  let m = null;
  for (const r of rows || []) {
    const d = dayjs(r.date);
    if (!m || d.isAfter(m)) m = d;
  }
  return m;
}

/* ------------------------------------------------------------------
 * Deal → Coupon Schedule
 * ------------------------------------------------------------------ */

/**
 * Map a ds_deals row to the subset of form fields that buildDebtServiceSchedule expects.
 * buildDebtServiceSchedule expects camelCase keys:
 *  - datedDate, firstCouponDate, finalMaturityDate, couponFrequency, dayCount
 */
export function mapDealToScheduleForm(dealRow) {
  if (!dealRow) return null;

  const datedDate = dealRow.dated_date || dealRow.datedDate || null;
  const firstCouponDate =
    dealRow.first_coupon_date || dealRow.firstCouponDate || null;
  const finalMaturityDate =
    dealRow.final_maturity_date || dealRow.finalMaturityDate || null;

  return {
    datedDate,
    firstCouponDate,
    finalMaturityDate,
    couponFrequency:
      dealRow.coupon_frequency || dealRow.couponFrequency || "semiannual",
    dayCount: dealRow.day_count || dealRow.dayCount || "30/360",
  };
}

/**
 * Build issue-level coupon schedule from a persisted ds_deals row.
 * Uses deal.structure as the maturity rows array.
 */
export function buildIssueCouponScheduleFromDeal(dealRow) {
  const form = mapDealToScheduleForm(dealRow);
  if (!form?.datedDate || !form?.firstCouponDate || !form?.finalMaturityDate)
    return [];

  const structureRows = safeJson(dealRow.structure, []);
  const rows = Array.isArray(structureRows) ? structureRows : [];
  if (!rows.length) return [];

  try {
    const couponSchedule =
      buildDebtServiceSchedule({ rows, form }) || [];

    // ------------------------------------------------------------------
    // TEMP DEBUG: inspect structure + coupon schedule rows
    // Enable in console:
    //   localStorage.setItem("ds_debug_structure", "1")
    // Disable:
    //   localStorage.removeItem("ds_debug_structure")
    // ------------------------------------------------------------------
    if (
      typeof window !== "undefined" &&
      window?.localStorage?.getItem("ds_debug_structure") === "1"
    ) {
      window.__dsLoggedStructureKeys =
        window.__dsLoggedStructureKeys || {};

      if (!window.__dsLoggedStructureKeys[dealRow?.id]) {
        const keySet = new Set();
        for (const r of rows) {
          if (!r || typeof r !== "object") continue;
          Object.keys(r).forEach((k) => keySet.add(k));
        }
        window.__dsLoggedStructureKeys[dealRow?.id] = true;
      }
    }

    return couponSchedule;
  } catch (e) {
    console.warn(
      "⚠️ buildDebtServiceSchedule failed for deal",
      dealRow?.id,
      e
    );
    return [];
  }
}

/* ------------------------------------------------------------------
 * Anchored Annual Period Utilities (Generic)
 * ------------------------------------------------------------------ */

/**
 * Build annual periods anchored to a "period end" date (month/day).
 * Generic version of prior fiscal-year logic.
 *
 * Period inclusion rule (consistent everywhere):
 *   d > period.start  AND  d <= period.end
 *
 * The first period always starts at startDate.
 */
export function buildAnnualPeriods(
  startDate,
  periodEndAnchor,
  lastCashflowDate
) {
  if (!startDate || !periodEndAnchor || !lastCashflowDate) return [];

  const start = dayjs(startDate);
  const lastPayment = dayjs(lastCashflowDate);

  const anchor = dayjs(periodEndAnchor);
  const anchorMonth = anchor.month();
  const anchorDay = anchor.date();

  // First anchor after the start date
  let firstEnd = start.month(anchorMonth).date(anchorDay);
  if (!firstEnd.isAfter(start, "day"))
    firstEnd = firstEnd.add(1, "year");

  const periods = [];
  let pStart = start.clone();
  let pEnd = firstEnd.clone();

  while (pEnd.isBefore(lastPayment, "day")) {
    periods.push({ start: pStart.clone(), end: pEnd.clone() });
    pStart = pEnd.clone();
    pEnd = pEnd.add(1, "year");
  }

  // Always include one more bucket
  periods.push({ start: pStart.clone(), end: pEnd.clone() });

  return periods;
}

/**
 * Find index of the period containing `asOfDate`
 * using inclusion rule: (d > start) && (d <= end)
 */
function findCurrentPeriodIndex(periods, asOfDate) {
  if (!periods?.length) return 0;
  const t = dayjs(asOfDate);

  if (t.isBefore(periods[0].start, "day")) return 0;

  const idx = periods.findIndex((p) => {
    const afterStart = t.isAfter(p.start, "day");
    const onOrBeforeEnd =
      t.isBefore(p.end, "day") || t.isSame(p.end, "day");
    return afterStart && onOrBeforeEnd;
  });

  return idx >= 0 ? idx : Math.max(0, periods.length - 1);
}

/**
 * Convert coupon schedule into annual totals (principal/interest/total),
 * starting with the period that contains today (default).
 */
export function couponToAnnualTotals({
  couponSchedule = [],
  datedDate,
  periodEndAnchor,
  asOfDate = null,
}) {
  if (!couponSchedule?.length || !datedDate || !periodEndAnchor)
    return [];

  const last = maxDate(couponSchedule);
  if (!last) return [];

  const allPeriods = buildAnnualPeriods(
    datedDate,
    periodEndAnchor,
    last
  );
  if (!allPeriods.length) return [];

  const asOf = asOfDate ? dayjs(asOfDate) : dayjs();
  const startIdx = findCurrentPeriodIndex(allPeriods, asOf);
  const periods = allPeriods.slice(startIdx);

  return periods.map((p) => {
    const inPeriod = couponSchedule.filter((r) => {
      const d = dayjs(r.date);
      return (
        d.isAfter(p.start, "day") &&
        (d.isBefore(p.end, "day") ||
          d.isSame(p.end, "day"))
      );
    });

    const principalRaw = inPeriod.reduce(
      (s, r) => s + num(r.principal),
      0
    );
    const interestRaw = inPeriod.reduce(
      (s, r) => s + num(r.interest),
      0
    );

    const principal = round2(principalRaw);
    const interest = round2(interestRaw);

    return {
      date: p.end.format("YYYY-MM-DD"),
      principal,
      interest,
      total: round2(principal + interest),
    };
  });
}

/**
 * Build portfolio annual datasets.
 *
 * Return contract preserved for compatibility:
 * {
 *   issues,
 *   aggregateFY,
 *   stackedByIssueFY
 * }
 */
export function buildPortfolioDebtService({
  members = [],
  periodEndOverride = null,
}) {
  const issues = [];

  for (const m of members || []) {
    const deal =
      m?.ds_deals || m?.ds_deals_row || m?.deal || null;
    if (!deal?.id) continue;
    if (m?.include === false) continue;

    const couponSchedule =
      buildIssueCouponScheduleFromDeal(deal);

    const periodEnd =
      periodEndOverride ||
      deal.fiscal_year_end ||
      deal.fiscalYearEnd ||
      "";

    const datedDate =
      deal.dated_date || deal.datedDate || null;

    const annualTotals = couponToAnnualTotals({
      couponSchedule,
      datedDate,
      periodEndAnchor: periodEnd,
    });

    const issueName =
      deal?.name ||
      deal?.deal_name ||
      "(Untitled Issue)";
    const label = issueName;

    issues.push({
      issueId: deal.id,
      label,
      deal,
      couponSchedule,
      annualTotals,
    });
  }

  const annualDates = new Set();
  for (const it of issues) {
    for (const r of it.annualTotals || [])
      annualDates.add(r.date);
  }

  const sortedDates = Array.from(annualDates).sort(
    (a, b) => new Date(a) - new Date(b)
  );

  const aggregateAnnual = sortedDates.map((date) => {
    let principalRaw = 0;
    let interestRaw = 0;

    for (const it of issues) {
      const row =
        (it.annualTotals || []).find(
          (x) => x.date === date
        );
      if (!row) continue;
      principalRaw += num(row.principal);
      interestRaw += num(row.interest);
    }

    const principal = round2(principalRaw);
    const interest = round2(interestRaw);

    return {
      date,
      principal,
      interest,
      total: round2(principal + interest),
    };
  });

  const stackedByIssueAnnual = sortedDates.map(
    (date) => {
      const out = { date };
      let grandRaw = 0;

      for (const it of issues) {
        const key = `issue_${it.issueId}`;
        const row =
          (it.annualTotals || []).find(
            (x) => x.date === date
          );
        const v = row ? num(row.total) : 0;
        const vr = round2(v);
        out[key] = vr;
        grandRaw += vr;
      }

      out.total = round2(grandRaw);
      return out;
    }
  );

  return {
    issues,
    aggregateFY: aggregateAnnual,
    stackedByIssueFY: stackedByIssueAnnual,
  };
}

/**
 * Flatten issue coupon schedules into payment-date detail for a Date/Period report.
 * Date boundaries are inclusive and intentionally have no relationship to "today",
 * which allows the report to cover both historical and future scheduled payments.
 */
export function buildPortfolioDebtServicePeriodRows({
  issues = [],
  reportType = "date",
  selectedDate = "",
  startDate = "",
  endDate = "",
}) {
  const from = reportType === "period" ? startDate : selectedDate;
  const to = reportType === "period" ? endDate : selectedDate;
  if (!from || !to || from > to) return [];

  const rows = [];

  for (const [issueIndex, issue] of (issues || []).entries()) {
    for (const payment of issue?.couponSchedule || []) {
      const date = dayjs(payment?.date);
      if (!date.isValid()) continue;

      const dateIso = date.format("YYYY-MM-DD");
      if (dateIso < from || dateIso > to) continue;

      const principal = round2(payment?.principal);
      const interest = round2(payment?.interest);
      const total = round2(principal + interest);
      if (Math.abs(total) < 0.005) continue;

      rows.push({
        date: dateIso,
        issue: issue?.label || "Issue",
        issue_id: issue?.issueId || "",
        principal,
        interest,
        total,
        _issue_order: issueIndex,
      });
    }
  }

  return rows.sort(
    (a, b) =>
      a.date.localeCompare(b.date) ||
      a._issue_order - b._issue_order ||
      a.issue.localeCompare(b.issue)
  );
}
